/**
 * Owner-only panels for /tenants — clustered together because they
 * share the same data-style (paginated, last-N, status badges).
 *
 *   • AutomationKillSwitchPanel — one toggle that mutes ALL outbound
 *     dispatches (emails, webhooks, briefings, digests) for the
 *     selected tenant. Records who pressed it + a reason.
 *
 *   • BriefingWebhooksPanel — per-tenant Slack/Teams briefing webhook
 *     CRUD (mirrors OutboundWebhooksPanel but daily-briefing-formatted).
 *
 *   • BoardPackEngagementPanel — open/click rollup for the weekly
 *     board-pack PDF email so the Owner can see who's actually
 *     reading the most carefully-prepared output.
 *
 *   • WhatsNewDebugPanel — quick view of every release note + its
 *     dismissed state for the current user (useful when debugging the
 *     banner across role-scoped audiences).
 */
import { useEffect, useState } from "react";
import {
  Power, PowerOff, Webhook, Plus, Trash2, PlayCircle, Mail, Eye, MousePointerClick,
  BellRing, Video, RefreshCw, Flame, Download, FileBarChart, Send,
  Activity, AlertTriangle, CheckCircle2, Gauge, History, XCircle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_TENANT = "tnt_default";

// ============================================================ kill-switch
export function AutomationKillSwitchPanel() {
  const [tenant] = useState(DEFAULT_TENANT);
  const [state, setState] = useState(null);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const r = await api.get(`/owner/automation/status?tenant_id=${tenant}`);
      setState(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e)); }
  };
  useEffect(() => { load(); }, []);

  const flip = async (next) => {
    try {
      const path = next ? "/owner/automation/pause" : "/owner/automation/resume";
      await api.post(path, { tenant_id: tenant, reason });
      toast.success(next ? "All outbound automation paused" : "Outbound automation resumed");
      setReason("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const paused = !!state?.paused;
  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="automation-kill-switch-panel"
    >
      <div className="flex items-start gap-3">
        <div className={`rounded-lg p-2 ${paused ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
          {paused ? <PowerOff size={18} /> : <Power size={18} />}
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Automation kill-switch
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            One click silences <strong>every</strong> outbound automated dispatch for{" "}
            <code className="font-mono text-[11px]">{tenant}</code> — emails, risk-rescore
            webhooks, Slack/Teams briefings, ROI follow-ups, connector health digests. Use
            before a sensitive demo or during incident response.
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
            paused ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
          }`}
          data-testid="kill-switch-state-badge"
        >
          {paused ? "Paused" : "Active"}
        </span>
      </div>

      {err && <p className="mt-3 text-xs text-rose-600">{err}</p>}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional, audited)"
          className="flex-1 rounded-md border border-black/10 px-3 py-1.5 text-xs focus:border-ek-purple focus:outline-none"
          data-testid="kill-switch-reason-input"
        />
        {!paused ? (
          <button
            onClick={() => flip(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
            data-testid="kill-switch-pause-button"
          >
            <PowerOff size={14} /> Pause all outbound automation
          </button>
        ) : (
          <button
            onClick={() => flip(false)}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            data-testid="kill-switch-resume-button"
          >
            <Power size={14} /> Resume outbound automation
          </button>
        )}
      </div>

      {state?.history?.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ek-ink/50">History</p>
          <ul className="mt-2 space-y-1 text-[12px]" data-testid="kill-switch-history">
            {state.history.slice(0, 5).map((h, i) => (
              <li key={i} className="flex items-center gap-2 text-ek-ink/65">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${h.paused ? "bg-rose-500" : "bg-emerald-500"}`} />
                <span>{h.paused ? "Paused" : "Resumed"}</span>
                <span className="font-mono text-[11px] text-ek-ink/45">{h.ts?.slice(0, 19)}</span>
                {h.set_by && <span className="text-ek-ink/45">· {h.set_by}</span>}
                {h.reason && <span className="italic text-ek-ink/55">— {h.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ============================================================ briefing webhooks
export function BriefingWebhooksPanel() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState({
    url: "", tenant_id: DEFAULT_TENANT, platform: "teams", active: true,
  });

  const load = async () => {
    setErr(null);
    try { setItems((await api.get("/owner/briefing-webhooks")).data.rows || []); }
    catch (e) { setErr(formatApiErrorDetail(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.url) { toast.error("Webhook URL required"); return; }
    try {
      await api.post("/owner/briefing-webhooks", draft);
      toast.success("Briefing webhook created");
      setDraftOpen(false);
      setDraft({ url: "", tenant_id: DEFAULT_TENANT, platform: "slack", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const toggle = async (wh) => {
    try { await api.patch(`/owner/briefing-webhooks/${wh.webhook_id}`, { active: !wh.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const fireTest = async (wh) => {
    try {
      const r = await api.post(`/owner/briefing-webhooks/${wh.webhook_id}/test`);
      if (r.data?.paused) toast.error("Tenant automation is paused — resume it first.");
      else if (r.data?.sent) toast.success(`Synthetic briefing dispatched (HTTP ${r.data.status})`);
      else toast.error(`Test failed: ${r.data?.error || r.data?.status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const remove = async (wh) => {
    if (!window.confirm(`Delete briefing webhook for ${wh.url}?`)) return;
    try { await api.delete(`/owner/briefing-webhooks/${wh.webhook_id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="briefing-webhooks-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple"><BellRing size={18} /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Daily briefing webhooks (Teams / Slack / generic)
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            Pipe the 07:00 UTC board briefing into a Microsoft Teams channel (or any
            HTTPS webhook receiver) via an Incoming Webhook URL — no OAuth, no app review.
            Slack works the same way if you ever wire one up.
          </p>
        </div>
        <button
          onClick={() => setDraftOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple/90"
          data-testid="briefing-webhook-add-toggle"
        >
          <Plus size={14} /> Add briefing
        </button>
      </div>

      {draftOpen && (
        <div className="mt-4 grid gap-2 rounded-xl border border-black/10 bg-slate-50/60 p-3 text-xs sm:grid-cols-2" data-testid="briefing-create-form">
          <label className="space-y-1 sm:col-span-2">
            <span className="text-ek-ink/65">Slack / Teams Incoming Webhook URL</span>
            <input
              value={draft.url}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              className="w-full rounded-md border border-black/10 px-2 py-1.5 font-mono text-xs focus:border-ek-purple focus:outline-none"
              placeholder="https://your-org.webhook.office.com/webhookb2/…  (Teams Incoming Webhook)"
              data-testid="briefing-url-input"
            />
          </label>
          <label className="space-y-1">
            <span className="text-ek-ink/65">Platform</span>
            <select
              value={draft.platform}
              onChange={(e) => setDraft({ ...draft, platform: e.target.value })}
              className="w-full rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs focus:border-ek-purple focus:outline-none"
              data-testid="briefing-platform-select"
            >
              <option value="teams">Microsoft Teams</option>
              <option value="generic">Generic JSON webhook</option>
              <option value="slack">Slack</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-ek-ink/65">Tenant</span>
            <input
              value={draft.tenant_id}
              onChange={(e) => setDraft({ ...draft, tenant_id: e.target.value })}
              className="w-full rounded-md border border-black/10 px-2 py-1.5 font-mono text-xs focus:border-ek-purple focus:outline-none"
              data-testid="briefing-tenant-input"
            />
          </label>
          <div className="flex items-center justify-end gap-2 sm:col-span-2">
            <button onClick={() => setDraftOpen(false)} className="rounded-md border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink/70 hover:text-ek-ink">Cancel</button>
            <button onClick={create} className="rounded-md bg-ek-ink px-2.5 py-1 text-xs font-semibold text-white hover:bg-black" data-testid="briefing-create-submit">Create</button>
          </div>
        </div>
      )}

      {err && <p className="mt-3 text-xs text-rose-600">{err}</p>}

      <div className="mt-4 space-y-2">
        {items.length === 0 && (
          <p className="text-xs text-ek-ink/50" data-testid="briefing-empty">
            No briefing webhooks yet. Paste a Slack or Teams Incoming Webhook URL above
            to start receiving the daily briefing in your channel.
          </p>
        )}
        {items.map((wh) => (
          <div key={wh.webhook_id} className="rounded-xl border border-black/5 bg-slate-50/40 p-3"
               data-testid={`briefing-row-${wh.webhook_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-xs text-ek-ink">{wh.url}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-ek-ink/55">
                  <span className="rounded bg-ek-purple/10 px-1.5 py-0.5 font-semibold text-ek-purple">{wh.platform}</span>
                  <span>{wh.tenant_id}</span>
                  <span>· daily 07:00 UTC</span>
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${wh.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                    {wh.active ? "active" : "paused"}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => fireTest(wh)} className="rounded-md border border-black/10 p-1.5 text-ek-ink/70 hover:border-ek-purple/40 hover:text-ek-purple"
                  data-testid={`briefing-test-${wh.webhook_id}`} title="Send synthetic test event"><PlayCircle size={14} /></button>
                <button onClick={() => toggle(wh)} className="rounded-md border border-black/10 p-1.5 text-ek-ink/70 hover:border-ek-purple/40 hover:text-ek-purple"
                  data-testid={`briefing-toggle-${wh.webhook_id}`} title={wh.active ? "Pause" : "Resume"}><Power size={14} /></button>
                <button onClick={() => remove(wh)} className="rounded-md border border-black/10 p-1.5 text-rose-600/80 hover:border-rose-300 hover:text-rose-600"
                  data-testid={`briefing-delete-${wh.webhook_id}`} title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-2 flex gap-4 text-[11px] text-ek-ink/55">
              <span>sent {wh.delivery_stats?.sent ?? 0}</span>
              <span>failed {wh.delivery_stats?.failed ?? 0}</span>
              {wh.delivery_stats?.last_at && <span>· last {wh.delivery_stats.last_at.slice(0, 19)}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================ board-pack engagement
export function BoardPackEngagementPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/owner/board-pack/engagement?limit=10")
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="board-pack-engagement-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple"><Mail size={18} /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Board-pack engagement
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            Open + click signal for the weekly board-pack PDF email. Tracked via a 1×1
            pixel + redirected CTA links. Tells you which board members actually look.
          </p>
        </div>
      </div>

      <div className="mt-4">
        {loading && <p className="text-xs text-ek-ink/50">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-xs text-ek-ink/50" data-testid="board-pack-engagement-empty">
            No delivery rows yet. Engagement appears here after the next Monday 07:00 UTC send,
            or after running <code className="font-mono text-[11px]">POST /owner/board-pack/weekly/run-now</code>.
          </p>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
                  <th className="py-1 pr-3">Tenant</th>
                  <th className="py-1 pr-3">Week</th>
                  <th className="py-1 pr-3">Recipients</th>
                  <th className="py-1 pr-3"><Eye className="inline" size={11} /> Opens</th>
                  <th className="py-1 pr-3"><MousePointerClick className="inline" size={11} /> Clicks</th>
                  <th className="py-1 pr-3">Rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const s = r.engagement_summary || {};
                  return (
                    <tr key={r.delivery_id} className="border-t border-black/5" data-testid={`engagement-row-${r.delivery_id}`}>
                      <td className="py-2 pr-3 font-medium text-ek-ink">{r.tenant_name || r.tenant_id}</td>
                      <td className="py-2 pr-3 font-mono text-ek-ink/55">{r.week_iso}</td>
                      <td className="py-2 pr-3 text-ek-ink/65">{s.recipients ?? 0}</td>
                      <td className="py-2 pr-3 text-emerald-700">{s.opens ?? 0}</td>
                      <td className="py-2 pr-3 text-ek-purple">{s.clicks ?? 0}</td>
                      <td className="py-2 pr-3 text-ek-ink/65">
                        {Math.round((s.open_rate || 0) * 100)}% / {Math.round((s.click_rate || 0) * 100)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}


// =================================================== demo-loop refresh
export function DemoLoopRefreshPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/owner/demo-loop/status");
      setData(r.data);
    } catch { /* tolerate */ }
  };
  useEffect(() => { load(); }, []);

  const refreshNow = async (force) => {
    setBusy(true);
    try {
      const r = await api.post(`/owner/demo-loop/refresh-now${force ? "?force=true" : ""}`);
      if (r.data.skipped === "pinned") {
        toast.info("Refresh skipped — Owner-curated MP4 is pinned. Use 'Force rebuild' to override.");
      } else if (r.data.ok) {
        toast.success(`Demo loop rebuilt (${r.data.size_kb} KB, ${(r.data.duration_ms/1000).toFixed(1)}s)`);
      } else {
        toast.error(r.data.error || "Demo loop refresh failed — see status history.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const file = data?.file;
  const lastRun = data?.history?.[0];
  const mtimeAgo = file ? ageString(file.mtime_epoch * 1000) : null;

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="demo-loop-refresh-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple"><Video size={18} /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">Login demo-loop</h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            The MP4 on <code className="font-mono text-[11px]">/login</code> is rebuilt weekly by
            a Playwright slideshow job. A pinned Owner-curated recording (e.g. Loom) takes
            precedence — use <b>Force rebuild</b> to override.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-black/5 bg-ek-paper p-3" data-testid="demo-loop-status-file">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">Current MP4</p>
          <p className="mt-1 text-sm font-semibold text-ek-ink">
            {file ? `${file.size_kb.toLocaleString()} KB` : "—"}
          </p>
          <p className="text-xs text-ek-ink/55">{mtimeAgo || "no file yet"}</p>
        </div>
        <div className="rounded-lg border border-black/5 bg-ek-paper p-3" data-testid="demo-loop-status-last">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">Last refresh</p>
          {lastRun ? (
            <>
              <p className={`mt-1 text-sm font-semibold ${lastRun.ok ? "text-emerald-700" : "text-rose-600"}`}>
                {lastRun.skipped === "pinned" ? "Skipped (pinned)" : (lastRun.ok ? "Success" : `Failed (${lastRun.error || "exit " + lastRun.exit_code})`)}
              </p>
              <p className="text-xs text-ek-ink/55">{ageString(Date.parse(lastRun.ts))}</p>
            </>
          ) : (
            <p className="mt-1 text-sm text-ek-ink/55">No runs yet</p>
          )}
        </div>
        <div className="rounded-lg border border-black/5 bg-ek-paper p-3" data-testid="demo-loop-status-history">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">History (last 10)</p>
          <p className="mt-1 text-sm font-semibold text-ek-ink">{data?.history?.length ?? 0} runs</p>
          <p className="text-xs text-ek-ink/55">
            {data?.history?.filter((h) => h.ok).length ?? 0} ok ·{" "}
            {data?.history?.filter((h) => !h.ok && h.skipped !== "pinned").length ?? 0} failed
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => refreshNow(false)}
          disabled={busy}
          data-testid="demo-loop-refresh-now-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-ek-ink transition-colors hover:border-ek-purple hover:text-ek-purple disabled:opacity-50"
        >
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
          {busy ? "Working…" : "Refresh now"}
        </button>
        <button
          onClick={() => refreshNow(true)}
          disabled={busy}
          data-testid="demo-loop-force-rebuild-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ek-purple-dark disabled:opacity-50"
          title="Bypass the .pinned sentinel and overwrite the Owner-curated MP4"
        >
          Force rebuild (overwrite pinned)
        </button>
      </div>
    </section>
  );
}

function ageString(ms) {
  if (!ms || Number.isNaN(ms)) return null;
  const diff = Date.now() - ms;
  if (diff < 0) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ============================================ ROI hot leads
function _draftMailto(row, gbp) {
  // Open the Owner's mail client with a pre-filled outreach draft.
  //
  // Template tier is chosen by the prospect's modelled annual value:
  //   • ≥ £5m   → "Worth a 20-min conversation?" — opens with the
  //               proof-point figure, leans on size of the prize.
  //   • £1m–£5m → leads with the pilot scope — fixed-fee, fixed-scope,
  //               written success criteria — for serious-but-cautious
  //               prospects.
  //   • <£1m    → maturity-assessment CTA — the figure isn't enough to
  //     (+ PDF)   anchor on, so we invite them to score their PPM
  //               maturity (free, 5 min, builds the case for them).
  //
  // We never receive the prospect's email from the public ROI endpoint
  // (no email captured) so the Owner pastes the recipient in — but
  // every figure they need is already in the body, ready to send in
  // under 30 seconds from signal → first touch.
  const co = (row.company || "").trim();
  const value = gbp(row.annual_value);
  const annual = row.annual_value || 0;
  const dl = row.source === "one-pager" ? "downloaded the ROI one-pager" : "modelled an annual value";
  const greeting = co
    ? `Hi — I noticed someone at ${co} just ${dl} on ekselens.com.`
    : `Hi — I noticed you just ${dl} on ekselens.com.`;
  const figures = `${gbp(row.revenue)} revenue · ${(row.suppliers || 0).toLocaleString()} suppliers · ${row.incidents || 0} risk incidents/yr`;

  let subj, bodyLines;

  if (annual >= 5_000_000) {
    // Tier A — the figure carries the email.
    subj = co
      ? `Worth a 20-min conversation? ERIO modelled ${value}/yr for ${co}`
      : `Worth a 20-min conversation? ERIO modelled ${value}/yr`;
    bodyLines = [
      greeting,
      "",
      `${value} a year is the figure ERIO modelled against your inputs (${figures}). That's the size of the prize sitting on the table whether you act on it or not — loss avoidance ${gbp(row.loss_avoidance)} + analyst-time efficiency ${gbp(row.efficiency_gain)}.`,
      "",
      "If even a third of that is real on your data, this is worth twenty minutes. I'll bring two redacted board-packs from comparable mid-cap groups so you can see exactly what the output looks like.",
      "",
      "Are you free next week? I can hold Tuesday 10:00 or Thursday 14:00 (UK).",
      "",
      "Best,",
      "Navin · ERIO · ekselens.com/pilot",
    ];
  } else if (annual >= 1_000_000) {
    // Tier B — pilot scope is the wedge.
    subj = co
      ? `Following up on your ERIO ROI estimate — ${value}/yr for ${co}`
      : `Following up on your ERIO ROI estimate — ${value}/yr`;
    bodyLines = [
      greeting,
      "",
      `On the figures you provided — ${figures} — ERIO modelled an illustrative annual value of ${value} (loss avoidance ${gbp(row.loss_avoidance)} + analyst-time efficiency ${gbp(row.efficiency_gain)}).`,
      "",
      "Happy to walk you through how we'd prove that on your own data inside an 8–12 week design-partner pilot — fixed-fee, fixed-scope, with the success criteria written upfront. No platform replacement, no rip-and-replace; we sit alongside your existing GRC / ServiceNow / spreadsheet stack and quantify what they can't.",
      "",
      "Are you free for a 20-minute call next week?",
      "",
      "Best,",
      "Navin · ERIO · ekselens.com/pilot",
    ];
  } else {
    // Tier C — anchor on maturity, not money.
    subj = co
      ? `Worth scoring your PPM maturity? — for ${co}`
      : `Worth scoring your PPM maturity?`;
    bodyLines = [
      greeting,
      "",
      `Thanks for downloading the one-pager. On the inputs you provided (${figures}), the modelled annual value comes in at ${value} — useful as a starting point, but the figure that actually moves your board is harder to model in five minutes.`,
      "",
      "Better starting point: spend 5 minutes scoring your PPM maturity (free, no signup) at https://www.ekselens.com/maturity-assessment. It gives you a benchmark against ~30 mid-cap peers, identifies the two biggest gaps, and tells you whether a pilot would even pay back inside a year. If it would, we talk; if it wouldn't, we don't.",
      "",
      "Either way, you get a useful artefact for your next steering committee.",
      "",
      "Best,",
      "Navin · ERIO · ekselens.com/pilot",
    ];
  }

  return `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
}

export function RoiHotLeadsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/roi/hot-leads")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const items = data?.items || [];
  const thresholdGbp = data?.threshold_gbp || 1_000_000;
  const topValue = data?.top_annual_value || 0;
  const gbp = (n) => "£" + Math.round(n || 0).toLocaleString();

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="roi-hot-leads-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-amber-100 p-2 text-amber-700"><Flame size={18} /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            ROI hot leads <span className="text-sm font-normal text-ek-ink/55">· last {data?.window_days || 30} days</span>
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            Visitors who modelled an estimated annual value ≥ {gbp(thresholdGbp)} or downloaded
            the ROI one-pager PDF. These are the prospects to prioritise for outreach.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-black/5 bg-ek-paper p-3" data-testid="roi-hot-leads-stat-downloads">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">PDF downloads</p>
          <p className="mt-1 text-lg font-semibold text-ek-ink">{data?.downloads ?? 0}</p>
        </div>
        <div className="rounded-lg border border-black/5 bg-ek-paper p-3" data-testid="roi-hot-leads-stat-estimates">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">Estimates ≥ {gbp(thresholdGbp)}</p>
          <p className="mt-1 text-lg font-semibold text-ek-ink">{data?.estimates_over_threshold ?? 0}</p>
        </div>
        <div className="rounded-lg border border-black/5 bg-ek-paper p-3" data-testid="roi-hot-leads-stat-top">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">Top annual value</p>
          <p className="mt-1 text-lg font-semibold text-ek-purple">{gbp(topValue)}</p>
        </div>
      </div>

      <div className="mt-4">
        {loading && <p className="text-xs text-ek-ink/50">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-xs text-ek-ink/50" data-testid="roi-hot-leads-empty">
            No qualifying ROI signals in the last 30 days. The endpoint surfaces estimates at or
            above the threshold and every PDF download (the latter is unconditional intent).
          </p>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1 pr-3">Source</th>
                  <th className="py-1 pr-3">Company</th>
                  <th className="py-1 pr-3">Revenue</th>
                  <th className="py-1 pr-3">Suppliers</th>
                  <th className="py-1 pr-3">Incidents/yr</th>
                  <th className="py-1 pr-3">Annual value</th>
                  <th className="py-1 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 25).map((r, i) => (
                  <tr key={i} className="border-t border-black/5" data-testid={`roi-hot-lead-row-${i}`}>
                    <td className="py-2 pr-3 font-mono text-ek-ink/55">{ageString(Date.parse(r.ts)) || "—"}</td>
                    <td className="py-2 pr-3">
                      {r.source === "one-pager" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-semibold text-ek-purple">
                          <Download size={10} /> PDF
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                          <FileBarChart size={10} /> Estimate
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-ek-ink">{r.company || <span className="text-ek-ink/40">—</span>}</td>
                    <td className="py-2 pr-3 text-ek-ink/65">{gbp(r.revenue)}</td>
                    <td className="py-2 pr-3 text-ek-ink/65">{(r.suppliers || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-ek-ink/65">{r.incidents || 0}</td>
                    <td className="py-2 pr-3 font-semibold text-ek-purple">{gbp(r.annual_value)}</td>
                    <td className="py-2 pr-3">
                      <a
                        href={_draftMailto(r, gbp)}
                        data-testid={`roi-hot-lead-draft-${i}`}
                        title={(() => {
                          const v = r.annual_value || 0;
                          if (v >= 5_000_000) return "Tier A — leads with the proof-point figure";
                          if (v >= 1_000_000) return "Tier B — leads with the pilot scope";
                          return "Tier C — leads with the maturity assessment CTA";
                        })()}
                        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors ${
                          (r.annual_value || 0) >= 5_000_000
                            ? "border-ek-purple bg-ek-purple text-white hover:bg-ek-purple-dark"
                            : (r.annual_value || 0) >= 1_000_000
                              ? "border-ek-purple/60 bg-ek-purple/10 text-ek-purple hover:bg-ek-purple/20"
                              : "border-black/10 bg-white text-ek-ink/75 hover:border-ek-purple hover:text-ek-purple"
                        }`}
                      >
                        <Send size={10} /> Draft
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length > 25 && (
              <p className="mt-2 text-[10px] text-ek-ink/40">
                Showing 25 of {items.length}. Pull the rest via{" "}
                <code className="font-mono">GET /api/roi/hot-leads</code>.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Role × annual-value funnel — driven by the nurture-email captures
          we started collecting in iter112. Only renders when at least one
          nurture row exists so unconfigured Resend doesn't show an empty
          dead grid. */}
      {data?.funnel?.total_captured > 0 && (
        <div className="mt-6 border-t border-black/5 pt-5" data-testid="roi-hot-leads-funnel">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold text-ek-ink">
              Role × value funnel
              <span className="ml-2 text-xs font-normal text-ek-ink/55">
                · {data.funnel.total_captured} nurture-email captures
              </span>
            </h3>
            <p className="text-[10px] text-ek-ink/45">Counts of visitors who supplied email + role</p>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="roi-hot-leads-funnel-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
                  <th className="py-1 pr-3">Role</th>
                  {(data.funnel.buckets || []).map((b) => (
                    <th key={b} className="py-1 pr-3 text-right">{b}</th>
                  ))}
                  <th className="py-1 pr-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.funnel.rows || []).map((row) => (
                  <tr key={row.role} className="border-t border-black/5"
                      data-testid={`roi-funnel-row-${row.role.toLowerCase().replace(/\s+/g, '-')}`}>
                    <td className="py-2 pr-3 font-medium text-ek-ink">{row.role}</td>
                    {(data.funnel.buckets || []).map((b) => {
                      const n = row.buckets?.[b] || 0;
                      // Heatmap shading — darker = more in that cell, capped
                      // at 5 so a single £25m+ doesn't drown everything else.
                      const intensity = Math.min(1, n / 5);
                      const bg = n === 0 ? "transparent" : `rgba(119, 55, 246, ${0.08 + 0.5 * intensity})`;
                      return (
                        <td key={b} className="py-2 pr-3 text-right tabular-nums"
                            style={{ backgroundColor: bg }}>
                          {n === 0 ? <span className="text-ek-ink/30">·</span> : n}
                        </td>
                      );
                    })}
                    <td className="py-2 pr-3 text-right font-semibold text-ek-purple">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top follow-up list — sorted by annual_value desc, capped at 10.
          The CRM playbook is: tier A (£5m+) gets a personal email today,
          tier B (£1m–£5m) goes into the pilot-scope nurture loop, the
          rest get the maturity assessment CTA. */}
      {(data?.top_followup_leads || []).length > 0 && (
        <div className="mt-6 border-t border-black/5 pt-5" data-testid="roi-hot-leads-followup">
          <h3 className="text-sm font-semibold text-ek-ink">Top 10 to follow up</h3>
          <p className="mt-0.5 text-[11px] text-ek-ink/55">
            Sorted by annual value. Sample Maturity radar PDF travels with the nurture email
            (24h dedup per recipient).
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1 pr-3">Email</th>
                  <th className="py-1 pr-3">Role</th>
                  <th className="py-1 pr-3">Company</th>
                  <th className="py-1 pr-3 text-right">Annual value</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_followup_leads || []).map((r, i) => (
                  <tr key={i} className="border-t border-black/5"
                      data-testid={`roi-followup-row-${i}`}>
                    <td className="py-2 pr-3 font-mono text-ek-ink/55">{ageString(Date.parse(r.ts)) || "—"}</td>
                    <td className="py-2 pr-3">
                      <a href={`mailto:${r.email}`} className="text-ek-purple hover:underline">{r.email}</a>
                    </td>
                    <td className="py-2 pr-3 text-ek-ink/65">{r.role || <span className="text-ek-ink/40">—</span>}</td>
                    <td className="py-2 pr-3 text-ek-ink/65">{r.company || <span className="text-ek-ink/40">—</span>}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-ek-purple">{gbp(r.annual_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}


// ============================================ Pilot health
const _HEALTH_STYLES = {
  green: { bg: "bg-emerald-100", text: "text-emerald-800", icon: CheckCircle2, label: "Healthy" },
  amber: { bg: "bg-amber-100", text: "text-amber-800", icon: AlertTriangle, label: "Losing momentum" },
  red:   { bg: "bg-rose-100",   text: "text-rose-800",   icon: AlertTriangle, label: "Going dark" },
};

function _HealthPill({ status }) {
  const s = _HEALTH_STYLES[status] || _HEALTH_STYLES.amber;
  const Icon = s.icon;
  return (
    <span
      data-testid={`pilot-health-pill-${status}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.bg} ${s.text}`}
    >
      <Icon size={10} /> {s.label}
    </span>
  );
}

function _DaysCell({ days, redAt = 14, amberAt = 7 }) {
  if (days === null || days === undefined) {
    return <span className="text-ek-ink/40">never</span>;
  }
  const colour =
    days >= redAt ? "text-rose-600" :
    days >= amberAt ? "text-amber-700" :
    "text-ek-ink/70";
  return (
    <span className={`font-mono ${colour}`}>
      {days === 0 ? "today" : days === 1 ? "1d" : `${days}d`}
    </span>
  );
}

export function PilotHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyTid, setBusyTid] = useState(null);
  const [preview, setPreview] = useState(null); // { tenant_id, organisation, tier, recipient, subject, html, ... }
  const [editSubject, setEditSubject] = useState("");
  const [editHtml, setEditHtml] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/owner/pilot-health")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openPreview = async (tid) => {
    setBusyTid(tid);
    try {
      const r = await api.get(`/owner/pilot-health/${tid}/check-in/preview`);
      setPreview(r.data);
      setEditSubject(r.data.subject || "");
      setEditHtml(r.data.html || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusyTid(null);
    }
  };

  const sendNow = async () => {
    if (!preview) return;
    setSending(true);
    try {
      const r = await api.post(`/owner/pilot-health/${preview.tenant_id}/check-in`, {
        subject: editSubject,
        html: editHtml,
      });
      if (r.data.skipped === "email_unconfigured") {
        toast.warning("Email isn't configured — set RESEND_API_KEY to enable check-ins.");
      } else if (r.data.sent) {
        toast.success(`Check-in sent (${r.data.tier}) → ${r.data.recipient}`);
        setPreview(null);
        load();
      } else {
        toast.error(r.data.error || "Send failed — see Sentry for details.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSending(false);
    }
  };

  const items = data?.items || [];
  const counters = data?.counters || { green: 0, amber: 0, red: 0 };

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="pilot-health-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-emerald-100 p-2 text-emerald-700"><Activity size={18} /></div>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">Pilot health</h2>
            <p className="mt-0.5 text-sm text-ek-ink/65">
              One-glance answer to <i>&ldquo;do I need to call the pilot today?&rdquo;</i> Red = going dark
              (≥14d silent). Amber = losing momentum. Green = active in the last 7 days.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="pilot-health-refresh-btn"
          className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-ek-ink/65 transition-colors hover:border-ek-purple hover:text-ek-purple disabled:opacity-50"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="pilot-health-stat-green">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">Healthy</p>
          <p className="mt-1 text-lg font-semibold text-emerald-700">{counters.green}</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="pilot-health-stat-amber">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Losing momentum</p>
          <p className="mt-1 text-lg font-semibold text-amber-700">{counters.amber}</p>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="pilot-health-stat-red">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-800">Going dark</p>
          <p className="mt-1 text-lg font-semibold text-rose-700">{counters.red}</p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
              <th className="py-1 pr-3">Tenant</th>
              <th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3" title="Days since the most recent risk was created in this tenant">Last risk</th>
              <th className="py-1 pr-3" title="Days since any user from this tenant logged in">Last login</th>
              <th className="py-1 pr-3" title="Total risks in this tenant (all-time)">Risks</th>
              <th className="py-1 pr-3" title="Risks created in the last 7 days">+7d</th>
              <th className="py-1 pr-3" title="Board-pack email opens in the last 30 days">BP opens 30d</th>
              <th className="py-1 pr-3">Adoption</th>
              <th className="py-1 pr-3">Reason</th>
              <th className="py-1 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="py-3 text-center text-ek-ink/50">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="py-3 text-center text-ek-ink/50">No tenants yet.</td></tr>
            )}
            {!loading && items.map((t) => (
              <tr key={t.tenant_id} className="border-t border-black/5" data-testid={`pilot-health-row-${t.tenant_id}`}>
                <td className="py-2 pr-3">
                  <div className="font-semibold text-ek-ink">{t.organisation}</div>
                  <div className="font-mono text-[10px] text-ek-ink/45">{t.tenant_id}</div>
                </td>
                <td className="py-2 pr-3"><_HealthPill status={t.health} /></td>
                <td className="py-2 pr-3"><_DaysCell days={t.days_since_last_risk_created} /></td>
                <td className="py-2 pr-3"><_DaysCell days={t.days_since_any_login} /></td>
                <td className="py-2 pr-3 text-ek-ink/65">{t.risks_total.toLocaleString()}</td>
                <td className="py-2 pr-3 font-semibold text-ek-ink">{t.risks_created_last_7d}</td>
                <td className="py-2 pr-3 text-ek-ink/65">{t.board_pack_opens_last_30d}</td>
                <td className="py-2 pr-3 text-ek-ink/65">{t.adoption_pct}%</td>
                <td className="py-2 pr-3 text-[10px] text-ek-ink/55">{t.health_reason}</td>
                <td className="py-2 pr-3">
                  {t.health !== "green" && (
                    <button
                      onClick={() => openPreview(t.tenant_id)}
                      disabled={busyTid === t.tenant_id}
                      data-testid={`pilot-checkin-${t.tenant_id}`}
                      title={t.health === "red"
                        ? "Preview + send a 15-min-call ask to this tenant's CRO/admin"
                        : "Preview + send a soft 'anything I can help with?' check-in"}
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors disabled:opacity-50 ${
                        t.health === "red"
                          ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                          : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                      }`}
                    >
                      <Send size={10} />
                      {busyTid === t.tenant_id ? "Loading…" : "Check in"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="pilot-checkin-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
        >
          <div className="flex max-h-[90vh] w-[680px] flex-col rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-black/5 p-5">
              <div>
                <h3 className="font-display text-base font-semibold text-ek-ink">
                  Preview check-in — {preview.organisation}
                </h3>
                <p className="mt-1 text-xs text-ek-ink/55">
                  <_HealthPill status={preview.tier} /> · to{" "}
                  <span className="font-mono text-ek-ink/75">{preview.recipient || "(no deliverable user)"}</span>
                </p>
                <p className="mt-1 text-[11px] text-ek-ink/50">
                  Tier was computed server-side from current health; recipient is the
                  tenant&apos;s CRO/admin (auto-resolved). Edit the subject or HTML body
                  below if you want to personalise — leave blank to use the template
                  as-is.
                </p>
              </div>
              <button
                onClick={() => setPreview(null)}
                disabled={sending}
                aria-label="Close preview"
                className="rounded-md p-1 text-ek-ink/55 hover:bg-black/5"
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              <label className="block">
                <span className="text-xs font-semibold text-ek-ink/70">Subject</span>
                <input
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  data-testid="pilot-checkin-subject"
                  maxLength={200}
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-ek-ink/70">
                  HTML body <span className="font-normal text-ek-ink/45">(rendered preview below)</span>
                </span>
                <textarea
                  rows={6}
                  value={editHtml}
                  onChange={(e) => setEditHtml(e.target.value)}
                  data-testid="pilot-checkin-html"
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 font-mono text-[11px]"
                />
              </label>
              <div className="rounded-lg border border-black/5 bg-ek-paper p-4">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">
                  Rendered preview
                </p>
                {/* Rendered preview of the (possibly edited) HTML */}
                <div
                  data-testid="pilot-checkin-rendered-preview"
                  dangerouslySetInnerHTML={{ __html: editHtml }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-black/5 p-4">
              <button
                onClick={() => setPreview(null)}
                disabled={sending}
                className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-ek-ink/70 hover:border-ek-ink/30 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={sendNow}
                disabled={sending || !preview.recipient || !preview.email_configured}
                data-testid="pilot-checkin-send-btn"
                title={
                  !preview.email_configured ? "Set RESEND_API_KEY to enable sending"
                    : !preview.recipient ? "No deliverable user for this tenant"
                    : `Send to ${preview.recipient}`
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple-dark disabled:opacity-50"
              >
                <Send size={12} />
                {sending ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


// ============================================ Tenant connector quota
export function TenantConnectorQuotaPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { tid, syncs, max }
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/owner/tenants/quota")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body = {};
      // Empty string → null (clear back to env default); numeric string → int.
      body.connectors_quota_per_day = editing.syncs === "" ? null : parseInt(editing.syncs, 10);
      body.max_connectors = editing.max === "" ? null : parseInt(editing.max, 10);
      await api.patch(`/owner/tenants/${editing.tid}/quota`, body);
      toast.success(`Quota updated for ${editing.tid}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const items = data?.items || [];
  const defaults = data?.defaults || {};
  const _pct = (used, total) => total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const _barColour = (pct) => pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="tenant-quota-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple"><Gauge size={18} /></div>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Tenant connector quota
            </h2>
            <p className="mt-0.5 text-sm text-ek-ink/65">
              Per-tenant caps on active connectors and rolling 24-hour sync rate.
              Defaults: {defaults.syncs_per_day || 100} syncs/day · {defaults.max_connectors || 10} connectors.
              Click <b>Set</b> to override for a specific pilot.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="tenant-quota-refresh-btn"
          className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-ek-ink/65 transition-colors hover:border-ek-purple hover:text-ek-purple disabled:opacity-50"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
              <th className="py-1 pr-3">Tenant</th>
              <th className="py-1 pr-3" title="Active connectors vs cap">Connectors</th>
              <th className="py-1 pr-3" title="Syncs in the last 24h vs cap">Syncs / 24h</th>
              <th className="py-1 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="py-3 text-center text-ek-ink/50">Loading…</td></tr>
            )}
            {!loading && items.map((t) => {
              const cnxPct = _pct(t.connectors_used, t.effective_max_connectors);
              const syncPct = _pct(t.syncs_used_24h, t.effective_quota_per_day);
              return (
                <tr key={t.tenant_id} className="border-t border-black/5" data-testid={`tenant-quota-row-${t.tenant_id}`}>
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-ek-ink">{t.organisation}</div>
                    <div className="font-mono text-[10px] text-ek-ink/45">{t.tenant_id}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-black/5">
                        <div className={`h-full ${_barColour(cnxPct)}`} style={{ width: `${cnxPct}%` }} />
                      </div>
                      <span className="font-mono text-ek-ink/70">
                        {t.connectors_used}/{t.effective_max_connectors}
                        {t.configured_max_connectors == null && (
                          <span className="ml-1 text-[10px] text-ek-ink/40">(default)</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-black/5">
                        <div className={`h-full ${_barColour(syncPct)}`} style={{ width: `${syncPct}%` }} />
                      </div>
                      <span className="font-mono text-ek-ink/70">
                        {t.syncs_used_24h}/{t.effective_quota_per_day}
                        {t.configured_quota_per_day == null && (
                          <span className="ml-1 text-[10px] text-ek-ink/40">(default)</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => setEditing({
                        tid: t.tenant_id,
                        syncs: t.configured_quota_per_day ?? "",
                        max: t.configured_max_connectors ?? "",
                      })}
                      data-testid={`tenant-quota-edit-${t.tenant_id}`}
                      className="rounded-md border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-ek-ink/65 transition-colors hover:border-ek-purple hover:text-ek-purple"
                    >
                      Set
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="tenant-quota-edit-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
        >
          <div className="w-[420px] rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="font-display text-base font-semibold text-ek-ink">
              Set quota — {editing.tid}
            </h3>
            <p className="mt-1 text-xs text-ek-ink/55">
              Leave blank to fall back to the env-controlled default
              ({defaults.syncs_per_day} syncs/day · {defaults.max_connectors} connectors).
            </p>
            <div className="mt-3 space-y-3">
              <label className="block">
                <span className="text-xs font-semibold text-ek-ink/70">Max connectors</span>
                <input
                  type="number"
                  min={1}
                  value={editing.max}
                  onChange={(e) => setEditing({ ...editing, max: e.target.value })}
                  data-testid="tenant-quota-input-max"
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm"
                  placeholder={`Default ${defaults.max_connectors}`}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-ek-ink/70">Syncs / 24h</span>
                <input
                  type="number"
                  min={1}
                  value={editing.syncs}
                  onChange={(e) => setEditing({ ...editing, syncs: e.target.value })}
                  data-testid="tenant-quota-input-syncs"
                  className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm"
                  placeholder={`Default ${defaults.syncs_per_day}`}
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-ek-ink/70 hover:border-ek-ink/30 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="tenant-quota-save-btn"
                className="rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple-dark disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================ Check-in history
export function CheckinHistoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/owner/pilot-health/check-in-history?limit=30")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const items = data?.items || [];

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="checkin-history-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple"><History size={18} /></div>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">Check-in history</h2>
            <p className="mt-0.5 text-sm text-ek-ink/65">
              Last 30 pilot check-ins sent across all tenants. Use this to dedupe
              (don&apos;t ping the same tenant twice in a week) and spot
              <i> &ldquo;sent amber, still amber a week later&rdquo;</i> patterns.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="checkin-history-refresh-btn"
          className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-ek-ink/65 transition-colors hover:border-ek-purple hover:text-ek-purple disabled:opacity-50"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-ek-ink/45">
              <th className="py-1 pr-3">When</th>
              <th className="py-1 pr-3">Tenant</th>
              <th className="py-1 pr-3" title="Tier at the moment we sent — may differ from current">Tier sent</th>
              <th className="py-1 pr-3" title="Tier right now — compare to 'Tier sent' to spot stagnation">Current</th>
              <th className="py-1 pr-3">Recipient</th>
              <th className="py-1 pr-3">Subject</th>
              <th className="py-1 pr-3" title="Did we edit the template before sending?">Edited?</th>
              <th className="py-1 pr-3" title="Did Resend accept the dispatch?">Sent?</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="py-3 text-center text-ek-ink/50">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="py-3 text-center text-ek-ink/50" data-testid="checkin-history-empty">
                  No check-ins sent yet. Try the &ldquo;Check in&rdquo; button on an Amber or Red row above.
                </td>
              </tr>
            )}
            {!loading && items.map((r, i) => {
              const stagnant = r.tier_at_send && r.current_tier && r.tier_at_send === r.current_tier && r.tier_at_send !== "green";
              return (
                <tr key={i} className="border-t border-black/5" data-testid={`checkin-history-row-${i}`}>
                  <td className="py-2 pr-3 font-mono text-ek-ink/55">{ageString(Date.parse(r.ts)) || "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-ek-ink">{r.organisation}</div>
                    <div className="font-mono text-[10px] text-ek-ink/45">{r.tenant_id}</div>
                  </td>
                  <td className="py-2 pr-3"><_HealthPill status={r.tier_at_send} /></td>
                  <td className="py-2 pr-3">
                    {r.current_tier ? <_HealthPill status={r.current_tier} /> : <span className="text-ek-ink/40">—</span>}
                    {stagnant && (
                      <span
                        className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700"
                        title="Still in the same non-green tier — the check-in didn't move the needle"
                      >
                        stagnant
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[10px] text-ek-ink/65">{r.recipient || "—"}</td>
                  <td className="py-2 pr-3 max-w-[280px] truncate text-ek-ink/75" title={r.subject || ""}>{r.subject || "—"}</td>
                  <td className="py-2 pr-3">
                    {r.edited ? (
                      <span className="rounded-full bg-ek-purple/10 px-1.5 py-0.5 text-[9px] font-semibold text-ek-purple">edited</span>
                    ) : (
                      <span className="text-ek-ink/40">template</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {r.sent ? (
                      <span className="inline-flex items-center gap-0.5 text-emerald-700">
                        <CheckCircle2 size={11} /> sent
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-0.5 text-rose-600"
                        title={r.error || "Send failed"}
                      >
                        <XCircle size={11} /> failed
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

